import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '@/modules/settings/api/settingsApi';
import {
  DEFAULT_VERTICAL,
  getVertical,
  isGeneralistSpecialty,
  type VerticalId,
  type VerticalOption,
} from '@/shared/constants/verticals';

/**
 * The current practice's vertical, for screens whose copy or options differ by
 * industry.
 *
 * Reads from the practice settings query, which is already cached app-wide, so
 * this costs nothing extra at the call site. While loading it returns the dental
 * default — every practice that existed before multi-vertical support is dental,
 * so that is the correct optimistic answer rather than a blank flash.
 *
 * Use this instead of comparing specialty strings inline: which specialty counts
 * as the generalist differs per vertical, and the old `=== 'general_dentistry'`
 * checks silently misclassify every medical practice as a specialist.
 */
export interface UseVerticalResult {
  vertical: VerticalOption;
  verticalId: VerticalId;
  specialty: string | null;
  isSpecialist: boolean;
  isLoading: boolean;
}

export function useVertical(): UseVerticalResult {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'practice'],
    queryFn: () => settingsApi.getPractice(),
    staleTime: 5 * 60 * 1000,
  });

  const verticalId = (data?.data?.vertical ?? DEFAULT_VERTICAL) as VerticalId;
  const specialty = data?.data?.specialty ?? null;

  return {
    vertical: getVertical(verticalId),
    verticalId,
    specialty,
    isSpecialist: !isGeneralistSpecialty(verticalId, specialty),
    isLoading,
  };
}
